/**
 * Mount-time migration for the tutor SplitView's persisted layout.
 *
 * Background (2026-04-20, multi-day debugging saga):
 *   1. The original bug: CourseLayout shipped with `minSize={0}` on the tutor
 *      SplitView.Pane. Users who dragged the tutor narrow had the percentage
 *      persisted by react-resizable-panels to localStorage. On reload with a
 *      wide viewport, the state machine stayed 'expanded' but the DOM honored
 *      the persisted tiny %. Tutor rendered as a ~40px text-clipped sliver.
 *
 *   2. First repair attempt (PR #23) used a useEffect migration. That was a
 *      race: react-resizable-panels reads the storage key synchronously
 *      during its own render, BEFORE any useEffect fires. The migration ran
 *      too late; SplitView had already committed to the poisoned layout.
 *
 *   3. Second repair attempt (PR #24) moved the migration into a useState
 *      lazy initializer and added an imperative resize(30) on cleared state.
 *      The synchronous timing was correct. The fix still did nothing.
 *
 *   4. ROOT CAUSE (this file) — the migration was targeting the WRONG
 *      localStorage key AND assumed the WRONG value format.
 *
 *      react-resizable-panels v4 wraps the user-supplied `storageKey` via a
 *      helper `he(id, panelIds)` that returns:
 *        `react-resizable-panels:${id}:${panelIds.join(":")}`
 *
 *      For our SplitView with id="iskool:course-content-tutor-split" and
 *      panel ids ["content", "tutor"], the real key is:
 *        `react-resizable-panels:iskool:course-content-tutor-split:content:tutor`
 *
 *      NOT the bare `iskool:course-content-tutor-split` string PR #23 and #24
 *      were scanning for.
 *
 *      The stored VALUE is also different from what we expected: an object
 *      keyed by panel id, e.g. `{"content":70,"tutor":30}`. Not an array, not
 *      `{ layout: [...] }`.
 *
 * This implementation handles both correctly and is defensive against panel
 * id changes or future library version tweaks: it matches by prefix so any
 * key starting with `react-resizable-panels:<storageKey>` gets considered,
 * and it accepts all three value shapes seen in the wild.
 */

const STORAGE_KEY_PREFIX = 'react-resizable-panels:iskool:course-content-tutor-split';
const LEGACY_STORAGE_KEY = 'iskool:course-content-tutor-split';
const TUTOR_MIN_PX = 320;
const CONTAINER_MIN_FOR_MIGRATION_PX = 400;

export type MigrationOutcome =
  | 'cleared-poisoned-layout'
  | 'cleared-corrupted-json'
  | 'cleared-unrecognized-shape'
  | 'no-op-no-storage'
  | 'no-op-container-too-narrow'
  | 'no-op-healthy';

export interface MigrationStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  key?(index: number): string | null;
  readonly length?: number;
}

/** Enumerate every storage key that matches the react-resizable-panels naming
 *  convention for our tutor SplitView, plus the legacy bare-string key left
 *  over from older code. Returns every key that could plausibly hold a
 *  persisted tutor layout. */
function findTutorSplitKeys(storage: MigrationStorage): string[] {
  const keys: string[] = [];
  // Enumerate via Storage API if available (browser localStorage).
  const len = storage.length ?? 0;
  const getKey = storage.key?.bind(storage);
  if (getKey && len > 0) {
    for (let i = 0; i < len; i++) {
      const k = getKey(i);
      if (k && k.startsWith(STORAGE_KEY_PREFIX)) keys.push(k);
    }
  }
  // Always also check the legacy bare-string key so the fallback-key path
  // covered by the original migration still works for any old bundle that
  // wrote it directly.
  if (storage.getItem(LEGACY_STORAGE_KEY)) keys.push(LEGACY_STORAGE_KEY);
  return keys;
}

/** Parse a layout blob into an array of pane percentages, ordered as stored.
 *  Handles the three shapes seen in the wild:
 *    - react-resizable-panels v4 object keyed by panel id (primary)
 *    - bare array `[70, 30]` (legacy)
 *    - `{ layout: [70, 30] }` (never seen in practice but handled defensively)
 *
 *  Returns `null` for unrecognized shapes. Corrupted JSON is caller's
 *  responsibility via try/catch. */
function extractPercentages(parsed: unknown): number[] | null {
  if (Array.isArray(parsed) && parsed.every((n) => typeof n === 'number')) {
    return parsed as number[];
  }
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.layout) && obj.layout.every((n) => typeof n === 'number')) {
      return obj.layout as number[];
    }
    const values = Object.values(obj);
    if (values.length >= 2 && values.every((n) => typeof n === 'number')) {
      return values as number[];
    }
  }
  return null;
}

export function migrateTutorSplitStorage(
  storage: MigrationStorage,
  containerPx: number,
): MigrationOutcome {
  const keys = findTutorSplitKeys(storage);
  if (keys.length === 0) return 'no-op-no-storage';

  if (containerPx < CONTAINER_MIN_FOR_MIGRATION_PX) {
    return 'no-op-container-too-narrow';
  }

  let outcome: MigrationOutcome = 'no-op-no-storage';

  for (const key of keys) {
    const raw = storage.getItem(key);
    if (!raw) continue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      storage.removeItem(key);
      outcome = outcome === 'cleared-poisoned-layout' ? outcome : 'cleared-corrupted-json';
      continue;
    }

    const percentages = extractPercentages(parsed);
    if (!percentages) {
      // Unrecognized shape — NUKE IT. If a future library version writes
      // something we don't understand, leaving it means we can't enforce the
      // pixel contract. Safer to wipe and let SplitView re-seed from its
      // defaultSize than to preserve something that might re-render a sliver.
      storage.removeItem(key);
      outcome =
        outcome === 'cleared-poisoned-layout' || outcome === 'cleared-corrupted-json'
          ? outcome
          : 'cleared-unrecognized-shape';
      continue;
    }

    // The tutor is the LAST entry in every storage format we've seen:
    //   [content, tutor] array
    //   { content: N, tutor: M } object (JS insertion order preserved)
    //   { layout: [content, tutor] }
    // Panel IDs are also in the real storage key suffix, so if we wanted
    // to be extra-precise we'd look up by id — but last-entry is what we
    // already relied on and it matches reality.
    const tutorPct = percentages[percentages.length - 1] ?? 0;
    const tutorPx = (tutorPct / 100) * containerPx;

    if (tutorPx > 0 && tutorPx < TUTOR_MIN_PX) {
      storage.removeItem(key);
      outcome = 'cleared-poisoned-layout';
    } else if (outcome !== 'cleared-poisoned-layout' && outcome !== 'cleared-corrupted-json') {
      outcome = 'no-op-healthy';
    }
  }

  return outcome;
}
